// R11-1 — THE TWO TAROT REPORT VIEWS, AND THE TWO CARD CONTROLS THEY ARE BUILT FROM.
//
// Eighth and ninth shells out of App.tsx, and they move for ONE reason: `ReportBody` — the
// DISPATCHER that decides which reading type reaches which view — cannot leave that file until every
// view it names has left it first. These are two of the four that were still holding it in.
//
// `TarotFlipCard` and `CardZoomOverlay` COME WITH THEM and both hold hooks, which is normally the
// line an extraction does not cross (R8: moving a stateful component puts it BEYOND the instrument,
// which is the one outcome roadmap 7 must never produce). R10-3 settled the test and it applies
// unchanged here: the line is whether the extraction LOSES REACH, not whether the file holds a hook.
// Both were already beyond the loader inside App.tsx, and the stubbed JSX runtime never CALLS a
// child component — it turns `<TarotFlipCard .../>` into a plain `{ type, props }`. So a caller that
// renders them stays fully callable and the gate reads the tree either way.
//
// `TarotReadingView` itself holds two hooks (the zoom target and which cards she has turned over), so
// it is NOT callable and the gate reaches it through the dispatcher as an element. That is the honest
// state of it: what R11 buys for this view is that the dispatch DECISION is checkable, not the view.
//
// Moved VERBATIM. `reportSectionCard` was already out (R9-5), `TarotCardImage` was already out
// (R8-4), and `NarrativeSection` — which a tarot SPREAD's sections are — was already out (R9-1).

import { useEffect, useState } from "react";
import { reportSectionCard } from "./ReportView";
import { useEscapeKey } from "./useEscapeKey";
import { TarotCardImage } from "./TarotCardImage";
import type { NarrativeSection } from "./NarrativeReportView";
import menuLogoUrl from "../assets/menu-logo.webp";

// A tarot SPREAD reading (the spread rooms' own reports), as opposed to the drawn-card reading below.
export type TarotSpreadData = {
  type: "tarot-spread";
  header: { name: string; dateText?: string; place?: string };
  kicker?: string;
  question?: string;
  numbers?: Array<{ label: string; value: string }>;
  overview: string;
  sections: NarrativeSection[];
};

export type TarotCard = { slug: string; name: string; position: string; orientation: string; interpretation: string };
export type TarotReadingData = {
  type: string;
  question: string;
  spread?: string;
  spreadLabel?: string;
  cards: TarotCard[];
  overview: string;
  evaluation?: string;
  interpreted?: boolean;
  // C1r: the draw's generation key. The server already auto-saved this reading under it,
  // so an explicit "Save to My Room" carries it back and folds into that same row.
  genKey?: string;
  autosaved?: boolean;
  // N3-1: set when this reading came from a photo of the member's own physical spread.
  analyzed?: boolean;
  photoUrl?: string;
};
// Click or tap any drawn card to see it full-size (its own X, Esc, or click-away to
// close). Nav-safe overlay that sits above the reading or the daily-card modal.
// OV2-4 — SHE TURNS HER OWN CARDS (owner, 2026-07-29).
//
// A drawn card arrives FACE DOWN and the member taps it over, one at a time, with a real turn. That is
// the moment at a reading table, and a whole-deck "reveal" button was standing in for it.
//
// ONE component, so the daily card, the three-card spread, the general pull and the timing spread
// cannot end up with three different turns. The rules it holds:
//   · the FRONT sits in normal flow, so the card's own art sizes the box and the back can never be a
//     different shape from the face it is hiding;
//   · REVERSED SURVIVES THE TURN. The rotation lives on the <img> (N4-1's one rule) and the turn lives
//     on an ancestor, so they are independent: a card that came out upside down lands upside down;
//   · once turned, the same tap is the zoom it has always been, so nothing she already knows is lost;
//   · a saved reading opens FACE UP. She turned those cards already, and making her do it again every
//     time she re-reads her own report would be a toy, not a ritual.
export function TarotFlipCard({ slug, name, orientation, imgClassName, faceDown, onZoom, onTurn }: {
  slug: string; name: string; orientation?: string; imgClassName?: string;
  faceDown?: boolean; onZoom?: () => void; onTurn?: () => void;
}) {
  const [turned, setTurned] = useState(!faceDown);
  // A new draw deals a new card into the same slot, so the turn resets with the card, not with the page.
  useEffect(() => { setTurned(!faceDown); }, [faceDown, slug]);
  return (
    <button
      type="button"
      className={turned ? "tarot-flip tarot-flip--turned" : "tarot-flip"}
      // `onTurn` is how a surface learns the card is face up — the caption underneath it must not
      // print the card's name while she is still looking at the back of it.
      onClick={() => { if (turned) onZoom?.(); else { setTurned(true); onTurn?.(); } }}
      // Face down, the label must NOT name the card: the point is that she has not seen it yet.
      aria-label={turned ? `Zoom ${name}` : "Turn this card over"}
    >
      <span className="tarot-flip__inner">
        <span className="tarot-flip__face tarot-flip__back" aria-hidden="true" />
        <span className="tarot-flip__face tarot-flip__front">
          <TarotCardImage className={imgClassName} slug={slug} name={name} orientation={orientation} loading="lazy" />
        </span>
      </span>
    </button>
  );
}

export function CardZoomOverlay({ src, alt, reversed, onClose }: { src: string; alt: string; reversed?: boolean; onClose: () => void }) {
  // R82 — **AND THIS IS THE COPY THAT HAD ALREADY DRIFTED, WHICH IS THE ARGUMENT FOR THE FOLD.** It
  // listed NO dependency (`}, []`), so the listener captured the first `onClose` this overlay was
  // ever handed and quietly ignored every one after it. Nothing throws; Escape simply stops working
  // on a zoomed card, which is exactly the class of failure that has no error state.
  useEscapeKey(onClose);
  return (
    <div className="card-zoom" role="dialog" aria-modal="true" aria-label={alt} onClick={onClose}>
      <button className="card-zoom__close" type="button" onClick={onClose} aria-label="Close">x</button>
      <img
        className={reversed ? "card-zoom__img is-reversed" : "card-zoom__img"}
        src={src}
        alt={alt}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
// Renders a tarot reading (live draw result and saved report) — the drawn cards
// as images in their positions, plus per-card and overall interpretation.
export function TarotReadingView({ data, faceDown }: { data: TarotReadingData; faceDown?: boolean }) {
  const overviewParas = (data.overview || "").split("\n").map((line) => line.trim()).filter(Boolean);
  const [zoom, setZoom] = useState<{ src: string; alt: string; reversed: boolean } | null>(null);
  const single = data.cards.length === 1;
  // OV2-4: a LIVE draw comes out face down and she turns each card herself. A saved reading re-opened
  // from a shelf does not — she turned those already. The photographed-spread reading never does
  // either: she is looking at cards she laid with her own hands on her own table.
  // The slot's NAME caption is held back until its card is turned, because a caption that reads
  // "The Tower · reversed" under a face-down card gives the whole thing away.
  const [turned, setTurned] = useState<Record<string, boolean>>({});
  const dealt = data.cards.map((c) => `${c.position}:${c.slug}`).join("|");
  useEffect(() => { setTurned({}); }, [dealt, faceDown]);
  return (
    <article className="chart-report tarot-reading">
      <header className="chart-report__band">
        <img src={menuLogoUrl} alt="12th House" className="chart-report__logo" />
        <p className="chart-report__kicker">Tarot Reading</p>
      </header>

      {data.question || data.spreadLabel ? (
        <section className="chart-report__info tarot-reading__meta">
          {data.question ? <p className="tarot-reading__question">“{data.question}”</p> : null}
          {data.spreadLabel ? <p className="tarot-reading__spread">{data.spreadLabel}</p> : null}
        </section>
      ) : null}

      {/* T3: each spread lays its cards out in its ACTUAL shape (celtic cross, tree of life,
          three-card row, timing) via a spread-specific grid class; cards are ORDERED to match. */}
      {/* N3-1: when the member photographed her own pull, her spread is shown back to her above the
          card art, so she can see for herself that the cards named are the cards she laid. */}
      {data.photoUrl ? (
        <figure className="tarot-reading__photo">
          <img src={data.photoUrl} alt="The spread you photographed" loading="lazy" />
          <figcaption>The spread you laid</figcaption>
        </figure>
      ) : null}

      <div className={`tarot-cards tarot-cards--${data.spread || "three-card"}${single ? " tarot-cards--single" : ""}`}>
        {data.cards.map((card, cardIndex) => {
          const src = card.slug ? `/api/assets/tarot/${card.slug}` : "";
          const reversed = card.orientation === "reversed";
          return (
            <figure key={card.position} className={reversed ? "tarot-card-slot is-reversed" : "tarot-card-slot"} style={{ ["--slot" as string]: cardIndex + 1 }}>
              <figcaption className="tarot-card-slot__pos">{card.position}</figcaption>
              {/* A card we hold no art for still reads. We never substitute another card's picture. */}
              {src ? (
                <TarotFlipCard
                  slug={card.slug}
                  name={card.name}
                  orientation={card.orientation}
                  imgClassName="tarot-card-slot__img"
                  faceDown={faceDown}
                  onZoom={() => setZoom({ src, alt: card.name, reversed })}
                  onTurn={() => setTurned((t) => ({ ...t, [card.position]: true }))}
                />
              ) : (
                <span className="tarot-card-slot__noart">{card.name}</span>
              )}
              {faceDown && src && !turned[card.position] ? (
                <figcaption className="tarot-card-slot__name tarot-card-slot__name--hidden">Tap to turn it over</figcaption>
              ) : (
                <figcaption className="tarot-card-slot__name">
                  {card.name}
                  {reversed ? " · reversed" : ""}
                </figcaption>
              )}
            </figure>
          );
        })}
      </div>

      {zoom ? (
        <CardZoomOverlay src={zoom.src} alt={zoom.alt} reversed={zoom.reversed} onClose={() => setZoom(null)} />
      ) : null}

      <section className="chart-report__reading">
        {overviewParas.length ? <h3>The Reading</h3> : null}
        {overviewParas.map((para, index) => (
          <p key={index} className="chart-report__overview">{para}</p>
        ))}
        <div className="chart-report__cards">
          {data.cards.map((card) => reportSectionCard(
            card.position,
            <>{card.position}: {card.name}{card.orientation === "reversed" ? " (reversed)" : ""}</>,
            card.interpretation,
          ))}
        </div>
      </section>

      {data.evaluation ? (
        <section className="chart-report__reading tarot-reading__evaluation">
          <h3>The Full Evaluation</h3>
          {data.evaluation.split("\n").map((line) => line.trim()).filter(Boolean).map((para, index) => (
            <p key={index} className="chart-report__overview">{para}</p>
          ))}
        </section>
      ) : null}
    </article>
  );
}

export function TarotSpreadView({ data }: { data: TarotSpreadData }) {
  const overviewParas = (data.overview || "").split("\n").map((line) => line.trim()).filter(Boolean);
  return (
    <article className="chart-report tarot-reading tarot-spread-report">
      <header className="chart-report__band">
        <img src={menuLogoUrl} alt="12th House" className="chart-report__logo" />
        <p className="chart-report__kicker">{data.kicker || "Tarot Reading"}</p>
      </header>

      {data.question ? (
        <section className="chart-report__info tarot-reading__meta">
          <p className="tarot-reading__question">“{data.question}”</p>
          {data.numbers?.length ? <p className="tarot-reading__spread">{data.numbers.map((n) => `${n.label}: ${n.value}`).join("  ·  ")}</p> : null}
        </section>
      ) : null}

      <section className="chart-report__reading">
        {overviewParas.length ? <h3>The Reading</h3> : null}
        {overviewParas.map((para, index) => (
          <p key={index} className="chart-report__overview">{para}</p>
        ))}
        <div className="chart-report__cards">
          {(data.sections || []).map((section, index) => (
            reportSectionCard(index, section.heading, section.body)
          ))}
        </div>
      </section>
    </article>
  );
}
